import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

import { useBreakpoint } from '@workspace/ui/hooks/useBreakpoint';

import { company } from '~/company/Company';
import { formatColors, formatVolume } from '~/components/ProductCard/utils';
import { useProductCardV3 } from '~/components/ProductCardV3/hooks/useProductCardV3';
import {
  AddToCartSection,
  ProductBadges,
  ProductColors,
  ProductImage,
  ProductInfo,
} from '~/components/ProductCardV3/shared';
import type { ProductCardVariantProps } from '~/components/ProductCardV3/types';
import { useProductSnippetAnalytics } from '~/components/ProductListAnalytics';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';

import styles from './ProductCardM.module.scss';

/**
 * ProductCardM - Medium size product card (179px mobile, 227px web)
 * Card shows only main offer (no SKU selection)
 */
const ProductCardM = ({
  product,
  index,
  className,
  showColorVariants = false,
  hideButton = false,
  productState,
  disabled = false,
  priority = false,
}: ProductCardVariantProps) => {
  const { handleClickProduct } = useCategoryAnalytics();

  const { isMobile } = useBreakpoint();

  const {
    currentItem,
    offerSku,
    limit,
    limitReachedText,
    image,
    oldPrice,
    currentPrice,
    totalCartPrice,
    cartItemsCount,
    isFavorite,
    isOutOfStock,
    colorVariants,
    badges,
    multiBuyInfo,
    mixAndMatch,
    productHasManyVariants,
    isBundle,
    selectOffer,
    toggleFavorite,
    changeCount,
    openSelectSKUModal,
  } = useProductCardV3(
    productState?.data ?? product,
    productState?.selectedOffer,
    'm',
  );

  const { snippetRef, trackSnippetClick } = useProductSnippetAnalytics(
    currentItem,
    index,
  );

  const handleProductClick = useCallback(() => {
    trackSnippetClick();
    handleClickProduct(product);
  }, [product, handleClickProduct, trackSnippetClick]);

  const volume = formatVolume(currentItem.properties);
  const colors = formatColors(currentItem.properties);

  const hasDiscount = oldPrice && oldPrice !== currentPrice;

  return (
    <div
      ref={snippetRef}
      className={classes([styles.card, disabled && styles.disabled, className])}
      data-company={company.name}
    >
      <div className="relative w-full">
        <ProductImage
          src={image}
          alt={currentItem.name}
          size="m"
          badges={
            badges.length ? (
              <ProductBadges
                variant={company.match({
                  mcosmetic: 'brandOutline',
                  default: 'primary',
                })}
                badges={badges}
                size={company.match({
                  mcosmetic: isMobile ? 'xs' : 'm',
                  default: 's',
                })}
              />
            ) : null
          }
          isFavorite={isFavorite}
          onToggleFavorite={toggleFavorite}
          productSlug={product.slug}
          offerSku={offerSku}
          onProductClick={handleProductClick}
          cartItemsCount={cartItemsCount}
          currentPrice={currentPrice}
          totalCartPrice={totalCartPrice}
          isOutOfStock={isOutOfStock}
          showGradientOverlay={!company.isSaas}
          priority={priority}
        />
        <ProductColors
          colors={colors}
          className="absolute right-[9px] bottom-[8px] z-1"
        />
      </div>

      <div className={styles.infoWrapper}>
        <ProductInfo
          onProductClick={handleProductClick}
          name={currentItem.name}
          volume={volume}
          rating={currentItem.ratingAverage}
          reviewsCount={currentItem.ratingMarksCount}
          isRatingShown={false}
          size="m"
          colorVariants={
            showColorVariants && colorVariants.length > 1
              ? colorVariants.map((v) => ({
                  color: v.value,
                  sku: v.sku,
                  name: v.value,
                  image: v.image,
                }))
              : undefined
          }
          onVariantSelect={selectOffer}
          productSlug={product.slug}
          currentSku={offerSku}
          multiBuyInfo={multiBuyInfo}
          cartItemsCount={cartItemsCount}
          promoRequiredQuantity={currentItem.getLowestMultibuyLevel()?.quantity}
        />

        {!hideButton && (
          <AddToCartSection
            currentPrice={mixAndMatch?.priceFrom ?? currentPrice}
            oldPrice={
              mixAndMatch
                ? mixAndMatch.regularPrice
                : hasDiscount
                  ? oldPrice
                  : undefined
            }
            isFromPrice={Boolean(mixAndMatch)}
            cartItemsCount={cartItemsCount}
            limit={limit}
            limitReachedText={limitReachedText}
            size="m"
            onChangeCount={changeCount}
            productId={productState?.data?.id ?? product?.id ?? 0}
            sku={productState?.selectedOffer?.sku ?? currentItem.sku ?? ''}
            isOutOfStock={isOutOfStock}
            productSlug={product.slug}
            productHasManyVariants={productHasManyVariants}
            isBundle={isBundle}
            onOpenSelectSKUModal={openSelectSKUModal}
          />
        )}
      </div>

      {disabled && <div className={styles.disabledOverlay} />}
    </div>
  );
};

export default observer(ProductCardM);
